import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography
} from '@mui/material';
import {
  PictureAsPdf as PdfIcon,
  Refresh as RefreshIcon,
  OpenInNew as OpenInNewIcon
} from '@mui/icons-material';
import PedigreeExporter from '../services/pedigreeExporter';
import WebApi from '../services/webApi';
import { getBirdListLabels } from '../utils/birdSearch';
import { loadPdfInReservedTab, reservePdfTab } from '../utils/pdfWindow';

const formatDownloadedAt = (value) => {
  const date = new Date(Number(value));
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short'
  }).format(date);
};

const PdfHistory = () => {
  const navigate = useNavigate();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [generatingId, setGeneratingId] = useState(null);

  const loadHistory = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setHistory(await WebApi.getPdfHistory());
    } catch (loadError) {
      console.error('Error loading PDF history:', loadError);
      setError('Could not load downloaded pedigrees. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const viewPdf = async (entry) => {
    if (!entry.available || generatingId !== null) return;

    // Reserve the tab during the click. Browsers may block a tab opened only
    // after the asynchronous PDF generation has finished.
    const previewTab = reservePdfTab();
    if (!previewTab) {
      setError('Your browser blocked the PDF tab. Allow pop-ups for this site, then try again.');
      return;
    }

    setGeneratingId(entry.event_id);
    setError('');
    let viewUrl = '';
    try {
      const result = await PedigreeExporter.createPreviewForBirdId(entry.bird_id);
      viewUrl = result.viewUrl;
      if (!loadPdfInReservedTab(previewTab, viewUrl)) {
        URL.revokeObjectURL(viewUrl);
        viewUrl = '';
        throw new Error('Could not load PDF in the reserved browser tab');
      }
    } catch (viewError) {
      console.error('Error opening PDF from history:', viewError);
      try {
        previewTab.close();
      } catch (closeError) {
        // The browser may already own or have closed the reserved tab.
      }
      setError('Could not open this pedigree PDF in a new tab. Please try again.');
    } finally {
      setGeneratingId(null);
    }
  };

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 3 }}>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="h4" component="h1">Downloaded Pedigrees</Typography>
          <Typography color="text.secondary" sx={{ mt: 0.5 }}>
            Pedigrees previously downloaded from this account.
          </Typography>
        </Box>
        <Tooltip title="Refresh history">
          <IconButton
            onClick={loadHistory}
            disabled={loading}
            aria-label="Refresh downloaded pedigrees"
            sx={{ width: 44, height: 44 }}
          >
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ minHeight: 280, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress size={30} />
        </Box>
      ) : history.length === 0 ? (
        <Paper variant="outlined" sx={{ px: 3, py: 7, textAlign: 'center' }}>
          <PdfIcon sx={{ fontSize: 44, color: 'text.disabled', mb: 1.5 }} />
          <Typography variant="h6">No downloaded pedigrees yet</Typography>
          <Typography color="text.secondary" sx={{ mt: 0.75, mb: 2.5 }}>
            Download a pedigree PDF and it will appear here.
          </Typography>
          <Button variant="contained" onClick={() => navigate('/birds')}>View birds</Button>
        </Paper>
      ) : (
        <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
          {history.map((entry, index) => {
            const { displayName, ringLabel } = getBirdListLabels({
              name: entry.bird_name,
              ring_number: entry.ring_number
            });
            const isGenerating = generatingId === entry.event_id;
            return (
              <React.Fragment key={entry.event_id}>
                {index > 0 && <Divider />}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: { xs: 2, sm: 2.5 },
                    px: { xs: 2, sm: 2.5 },
                    py: 2
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexGrow: 1, minWidth: 0 }}>
                    <Box
                      aria-hidden
                      sx={{
                        width: 44,
                        height: 44,
                        flexShrink: 0,
                        display: 'grid',
                        placeItems: 'center',
                        borderRadius: 1.5,
                        bgcolor: 'error.light',
                        color: 'error.contrastText'
                      }}
                    >
                      <PdfIcon />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="subtitle1" sx={{ overflowWrap: 'anywhere' }}>
                        {displayName}
                      </Typography>
                      {ringLabel && (
                        <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
                          {ringLabel}
                        </Typography>
                      )}
                      <Stack direction="row" useFlexGap flexWrap="wrap" spacing={1} sx={{ mt: 0.75, alignItems: 'center' }}>
                        <Typography variant="caption" color="text.secondary">
                          {formatDownloadedAt(entry.exported_at)}
                        </Typography>
                        {entry.download_count > 1 && (
                          <Chip size="small" variant="outlined" label={`${entry.download_count} downloads`} />
                        )}
                        {!entry.available && (
                          <Chip size="small" color="default" label="Bird no longer available" />
                        )}
                      </Stack>
                    </Box>
                  </Box>
                  <Button
                    variant="contained"
                    startIcon={isGenerating ? <CircularProgress size={18} color="inherit" /> : <OpenInNewIcon />}
                    onClick={() => viewPdf(entry)}
                    disabled={!entry.available || generatingId !== null}
                    sx={{ minWidth: 112, minHeight: 44, alignSelf: { xs: 'stretch', sm: 'center' } }}
                  >
                    {isGenerating ? 'Opening...' : 'Open PDF'}
                  </Button>
                </Box>
              </React.Fragment>
            );
          })}
        </Paper>
      )}
    </Box>
  );
};

export default PdfHistory;
